import {
  getUserPreference,
  saveUserPreference
} from "../../utils/OPPApiHelper";

export type GridSortDirection = "asc" | "desc";

export interface GridSortDescriptor {
  columnName: string;
  sortType: GridSortDirection;
}

export interface ResolvedGridSortPreference {
  sorts: GridSortDescriptor[];
}

export interface GridSortPreferenceSaveResult {
  succeeded: boolean;
  error?: unknown;
}

const saveQueues = new Map<
  string,
  Promise<GridSortPreferenceSaveResult>
>();

const isGridSortDirection = (value: unknown): value is GridSortDirection =>
  value === "asc" || value === "desc";

export const validateGridSortDescriptors = (
  value: unknown,
  allowedColumns: readonly string[]
): GridSortDescriptor[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const allowed = new Set(allowedColumns);
  const seen = new Set<string>();

  return value.reduce<GridSortDescriptor[]>((validSorts, candidate) => {
    if (
      typeof candidate === "object"
      && candidate !== null
      && typeof candidate.columnName === "string"
      && allowed.has(candidate.columnName)
      && isGridSortDirection(candidate.sortType)
      && !seen.has(candidate.columnName)
    ) {
      seen.add(candidate.columnName);
      validSorts.push({
        columnName: candidate.columnName,
        sortType: candidate.sortType
      });
    }
    return validSorts;
  }, []);
};

export const deserializeGridSortPreference = (
  serializedValue: string,
  allowedColumns: readonly string[]
): GridSortDescriptor[] | null => {
  let payload: unknown;
  try {
    payload = JSON.parse(serializedValue);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  const sorts = Array.isArray(payload)
    ? payload
    : typeof payload === "object"
      && payload !== null
      && "version" in payload
      && payload.version === 1
      && "sorts" in payload
      ? payload.sorts
      : null;

  if (!Array.isArray(sorts)) {
    return null;
  }

  const validSorts = validateGridSortDescriptors(sorts, allowedColumns);
  // Only an explicitly empty list represents Clear Sorting, not a fully invalid list.
  return sorts.length > 0 && validSorts.length === 0 ? null : validSorts;
};

export const resolveGridSortPreference = async (
  preferenceKey: string,
  allowedColumns: readonly string[],
  defaultSorts: readonly GridSortDescriptor[],
  emptyMeansDefault = false
): Promise<ResolvedGridSortPreference> => {
  const savedValue = await getUserPreference(preferenceKey, true);
  const defaults = validateGridSortDescriptors(defaultSorts, allowedColumns);

  if (savedValue === null) {
    return { sorts: defaults };
  }

  const saved = deserializeGridSortPreference(savedValue, allowedColumns);
  if (saved === null) {
    console.warn("Ignoring invalid or unsupported saved grid sorting preference. Loading default sorting.", preferenceKey);
    return { sorts: defaults };
  }
  // A cleared (empty) saved sort falls back to the default when the grid requires one.
  return { sorts: saved.length === 0 && emptyMeansDefault ? defaults : saved };
};

export const persistGridSortPreference = async (
  preferenceKey: string,
  sorts: readonly GridSortDescriptor[]
): Promise<GridSortPreferenceSaveResult> => {
  const previousSave = saveQueues.get(preferenceKey);
  const nextSave = (previousSave
    ? previousSave.then(() => undefined, () => undefined)
    : Promise.resolve()
  ).then(async () => {
    try {
      const succeeded = await saveUserPreference(
        preferenceKey,
        JSON.stringify({ version: 1, sorts })
      );
      return succeeded
        ? { succeeded: true }
        : {
          succeeded: false,
          error: new Error("The user preference API did not save the grid sorting preference.")
        };
    } catch (error) {
      return { succeeded: false, error };
    }
  });

  saveQueues.set(preferenceKey, nextSave);
  const clearCompletedSave = () => {
    if (saveQueues.get(preferenceKey) === nextSave) {
      saveQueues.delete(preferenceKey);
    }
  };
  void nextSave.then(clearCompletedSave, clearCompletedSave);

  return nextSave;
};

export const updateGridSortDescriptors = (
  currentSorts: readonly GridSortDescriptor[],
  nextSort: GridSortDescriptor
): GridSortDescriptor[] => {
  const existingIndex = currentSorts.findIndex(
    sort => sort.columnName === nextSort.columnName
  );

  if (existingIndex < 0) {
    return [...currentSorts, nextSort];
  }

  return currentSorts.map((sort, index) =>
    index === existingIndex ? nextSort : sort
  );
};

const compareValues = (
  left: unknown,
  right: unknown,
  direction: GridSortDirection
): number => {
  if (left === null || left === undefined || left === "") {
    return right === null || right === undefined || right === ""
      ? 0
      : direction === "asc" ? 1 : -1;
  }
  if (right === null || right === undefined || right === "") {
    return direction === "asc" ? -1 : 1;
  }

  const leftText = String(left);
  const rightText = String(right);
  const datePattern = /^(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{4})/;
  const leftDate = datePattern.test(leftText) ? Date.parse(leftText) : Number.NaN;
  const rightDate = datePattern.test(rightText) ? Date.parse(rightText) : Number.NaN;
  let comparison: number;

  if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) {
    comparison = leftDate - rightDate;
  } else {
    comparison = leftText.localeCompare(rightText, undefined, {
      numeric: true,
      sensitivity: "base"
    });
  }

  return direction === "asc" ? comparison : -comparison;
};

export const applyGridSort = <T extends Record<string, unknown>>(
  data: readonly T[],
  sorts: readonly GridSortDescriptor[]
): T[] =>
  data
    .map((item, originalIndex) => ({ item, originalIndex }))
    .sort((left, right) => {
      for (const sort of sorts) {
        const comparison = compareValues(
          left.item[sort.columnName],
          right.item[sort.columnName],
          sort.sortType
        );
        if (comparison !== 0) {
          return comparison;
        }
      }
      return left.originalIndex - right.originalIndex;
    })
    .map(({ item }) => item);
